<template>
  <section
    class="mb-16 flex flex-col gap-6 rounded-md border border-light-gray p-4 shadow-primary"
  >
    <div class="flex justify-between">
      <h3 class="text-xl text-dark-gray">Rate Confirmation Notifications</h3>

      <button
        @click="onOpenModal"
        class="flex cursor-pointer items-center"
      >
        <h3 class="text-yellow-500">Add New Email</h3>
        <img src="@/assets/plus-yellow-icon.svg" />
      </button>
    </div>

    <p class="text-1md text-body-color">
      Add one or more email addresses to be notified of new rate confirmations
      sent to your company. Rate confirmations emailed to you by brokers using
      Highway will only notify the email address(es) provided below.
    </p>

    <div v-if="active_email_addresses?.length > 0">
      <p class="mb-3 text-1md text-dark-gray">Active Email Addresses</p>

      <div
        v-for="email in active_email_addresses"
        :key="email.id"
        class="mb-4 flex justify-between rounded-md border border-light-gray p-3 shadow"
      >
        <div>
          <p class="text-body-color">{{ email.value }}</p>
          <p class="mt-1 text-xs text-gray-400">
            Created on {{ formatCreatedAt(email.created_at) }}
          </p>
        </div>

        <img
          src="@/assets/trash-icon-2.svg"
          class="h-10 w-10 cursor-pointer"
          @click="onOpenRemoveModal(email)"
        />
      </div>
    </div>

    <div v-if="inactive_email_addresses?.length > 0">
      <p class="mb-3 text-1md text-dark-gray">Inactive Email Addresses</p>

      <div
        v-for="email in inactive_email_addresses"
        :key="email.id"
        class="mb-4 flex justify-between rounded-md border border-light-gray p-3 shadow"
      >
        <div>
          <p class="text-body-color">{{ email.value }}</p>
          <p class="mt-1 text-xs text-gray-400">
            Created on {{ formatCreatedAt(email.created_at) }}
          </p>
        </div>

        <Popper
          placement="top"
          :hover="true"
          :arrow="true"
          offset-distance="6"
        >
          <img
            src="@/assets/refresh-icon-2.svg"
            class="mr-2 mt-2 w-5 cursor-pointer"
            @click="onOpenToggleModal(email.id)"
          />
          <template #content>
            <div
              class="auto rounded-md bg-dark-gray px-2 py-1 text-left text-2xs text-light-gray"
            >
              <p>Restore</p>
            </div>
          </template>
        </Popper>
      </div>
    </div>
  </section>

  <add-rate-confirmation-email-modal
    v-if="showEmailModal"
    :visible="showEmailModal"
    @close="onCloseModal"
    @add="onAddEmail"
  >
  </add-rate-confirmation-email-modal>

  <confirm-delete-modal
    :visible="showRemoveModal"
    subtext="Are you sure you want to remove this email?"
    customButtonText="Yes, remove"
    @delete="handleRemoveRateConfirmationEmail"
    @close="onCloseRemoveModal"
  ></confirm-delete-modal>

  <confirm-delete-modal
    :visible="showToggleModal"
    subtext="Are you sure you want restore this email?"
    customButtonText="Yes, restore"
    @delete="handleToggleActiveEmail"
    @close="onCloseToggleModal"
  ></confirm-delete-modal>
</template>

<script>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import moment from 'moment';
import Popper from 'vue3-popper';
import AddRateConfirmationEmailModal from '@/components/modals/AddRateConfirmationEmailModal.vue';
import ConfirmDeleteModal from '@/components/modals/ConfirmDeleteModal.vue';

export default {
  name: 'RateConfirmationNotifications',
  components: {
    AddRateConfirmationEmailModal,
    ConfirmDeleteModal,
    Popper,
  },
  computed: {
    rateConfirmationEmails() {
      return this.$store.state.emails.emails;
    },
    active_email_addresses() {
      return this.rateConfirmationEmails?.filter((email) => email.is_active);
    },
    inactive_email_addresses() {
      return this.rateConfirmationEmails?.filter((email) => !email.is_active);
    },
  },
  methods: {
    formatCreatedAt(date) {
      return moment(date).format('MM/DD/YYYY [at] h:mma');
    },
  },
  async created() {
    const query = `?q[is_type_eq]=rate_confirmation&q[carrier_id_eq]=${this.$store.state.users.currentUser.company.carrier_id}`;
    await this.$store.dispatch('emails/getEmails', { query });

    await this.$store.dispatch(
      'onboardingPacket/getLatestCompletedOnboardingPacket'
    );
  },
  setup() {
    const store = useStore();
    const toast = useToast();

    const showEmailModal = ref(false);
    const showRemoveModal = ref(false);
    const showToggleModal = ref(false);
    const deletedModalEmail = ref('');
    const toggleModalEmail = ref('');

    const onOpenModal = () => {
      showEmailModal.value = true;
    };
    const onCloseModal = () => {
      showEmailModal.value = false;
    };
    const onOpenRemoveModal = (email) => {
      showRemoveModal.value = true;
      deletedModalEmail.value = email;
    };
    const onCloseRemoveModal = () => {
      showRemoveModal.value = false;
    };
    const onOpenToggleModal = (id) => {
      showToggleModal.value = true;
      toggleModalEmail.value = id;
    };
    const onCloseToggleModal = () => {
      showToggleModal.value = false;
    };

    const getRateConfirmationEmails = async () => {
      const query = `?q[is_type_eq]=rate_confirmation&q[carrier_id_eq]=${store.state.users.currentUser.company.carrier_id}`;
      await store.dispatch('emails/getEmails', { query });
    };

    const onAddEmail = async (email) => {
      const packetId =
        store.state.onboardingPacket?.onboarding_id ||
        store.state.emails.emails?.[0]?.email_addressable_id;
      try {
        const res = await store.dispatch('emails/createEmail', {
          carrierId: store.state.users.currentUser.company.carrier_id,
          isType: 'rate_confirmation',
          value: email,
          onboardingPackeId: packetId,
        });

        if ([200, 201, 204].includes(res?.status)) {
          getRateConfirmationEmails();

          toast?.open({
            type: 'info',
            message: 'Email Saved',
            position: 'top-right',
            duration: 5000,
          });
        }
      } catch (error) {
        console.error('Error creating email:', error);
        toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        onCloseModal();
      }
    };

    const handleToggleActiveEmail = async () => {
      try {
        const res = await store.dispatch('emails/toggleActiveEmail', {
          id: toggleModalEmail.value,
        });

        if ([200, 201, 204].includes(res?.status)) {
          getRateConfirmationEmails();

          toast?.open({
            type: 'info',
            message: 'Email successfully updated',
            position: 'top-right',
            duration: 5000,
          });
        }
      } catch (error) {
        console.log('remove error: ', error);
        toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        onCloseToggleModal();
      }
    };

    const handleRemoveRateConfirmationEmail = async () => {
      try {
        const res = await store.dispatch('emails/inactivateEmail', {
          id: deletedModalEmail.value.id,
        });

        if ([200, 201, 204].includes(res?.status)) {
          getRateConfirmationEmails();

          toast?.open({
            type: 'info',
            message: 'Email successfully removed',
            position: 'top-right',
            duration: 5000,
          });
        }
      } catch (error) {
        console.log('remove error: ', error);
        toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        onCloseRemoveModal();
      }
    };

    return {
      onOpenModal,
      onCloseModal,
      onAddEmail,
      showEmailModal,
      onOpenRemoveModal,
      onCloseRemoveModal,
      showRemoveModal,
      handleRemoveRateConfirmationEmail,
      showToggleModal,
      handleToggleActiveEmail,
      onOpenToggleModal,
      onCloseToggleModal,
    };
  },
};
</script>
